/**
 * Embudo free-join (canal gratis) — single source of truth:
 *
 *   CTA free → sheet (bienvenida + Clerk free)
 *     → /portal/completar-perfil?access_request=1&plan_tier=free&free_join=1
 *     → /portal/acceso-gratis (revela link grupo free)
 *
 * El link de Telegram NO se entrega hasta cuenta Clerk + perfil completo.
 * destination / country se guardan en sessionStorage al abrir el sheet.
 */

import {
  MEMBERSHIP_ROUTE,
  MEMBERSHIP_ROUTE_EN_COMPLETE_PROFILE,
} from "@/lib/membershipAccessFlow";

export const FREE_JOIN_SESSION = {
  destination: "af_free_join_destination",
  flow: "af_free_join_flow",
  country: "af_free_join_country",
  language: "af_free_join_language",
} as const;

/** Rutas de revelación del link free (post-perfil). */
export const FREE_JOIN_ROUTE = {
  ES: "/portal/acceso-gratis",
  EN: "/portal/free-access",
} as const;

export function persistFreeJoinContext(opts: {
  /** Si vacío, no pisa el destination ya guardado. */
  destinationUrl?: string | null;
  country?: string | null;
  language?: string | null;
}): void {
  if (typeof window === "undefined") return;
  try {
    const dest = (opts.destinationUrl || "").trim();
    if (dest) sessionStorage.setItem(FREE_JOIN_SESSION.destination, dest);
    sessionStorage.setItem(FREE_JOIN_SESSION.flow, "1");
    if (opts.country) {
      const c = opts.country.trim().toUpperCase().slice(0, 2);
      if (/^[A-Z]{2}$/.test(c)) {
        sessionStorage.setItem(FREE_JOIN_SESSION.country, c);
      }
    }
    if (opts.language) {
      sessionStorage.setItem(FREE_JOIN_SESSION.language, opts.language.trim());
    }
  } catch {
    /* private mode */
  }
}

export function readFreeJoinDestination(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(FREE_JOIN_SESSION.destination)?.trim();
    return v || null;
  } catch {
    return null;
  }
}

export function readFreeJoinCountry(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const v = sessionStorage.getItem(FREE_JOIN_SESSION.country)?.trim().toUpperCase();
    return v && /^[A-Z]{2}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

export function readFreeJoinLanguage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(FREE_JOIN_SESSION.language)?.trim() || null;
  } catch {
    return null;
  }
}

export function isFreeJoinFlowActive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return sessionStorage.getItem(FREE_JOIN_SESSION.flow) === "1";
  } catch {
    return false;
  }
}

export function clearFreeJoinFlowFlag(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(FREE_JOIN_SESSION.flow);
  } catch {
    /* ignore */
  }
}

function isEnglishLang(language?: string | null): boolean {
  const l = (language || "").trim().toLowerCase();
  return l === "en" || l.startsWith("en-") || l.startsWith("en_");
}

/**
 * Detecta si el form de completar-perfil es free-join (no VIP post-pago).
 * Query params ganan; sessionStorage solo refuerza si hay access_request o plan free.
 */
export function isFreeJoinProfileRequest(
  sp: URLSearchParams,
  opts?: { hasPaidSession?: boolean },
): boolean {
  if (opts?.hasPaidSession) return false;
  const freeJoinFlag = sp.get("free_join") === "1";
  const planFree = (sp.get("plan_tier") || "").trim().toLowerCase() === "free";
  const accessRequest = sp.get("access_request") === "1";
  if (freeJoinFlag && accessRequest) return true;
  if (planFree && accessRequest) return true;
  // Session leftover only if URL already marks free/access (no hijack VIP)
  if (accessRequest && (planFree || freeJoinFlag || isFreeJoinFlowActive())) {
    return true;
  }
  return false;
}

/** URL de completar perfil para free join. */
export function buildFreeJoinCompleteProfileUrl(opts?: {
  language?: string | null;
  country?: string | null;
}): string {
  const lang = opts?.language ?? readFreeJoinLanguage();
  const country = opts?.country ?? readFreeJoinCountry();
  const en = isEnglishLang(lang);
  const path = en
    ? MEMBERSHIP_ROUTE_EN_COMPLETE_PROFILE
    : MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO;
  const q = new URLSearchParams();
  q.set("access_request", "1");
  q.set("plan_tier", "free");
  q.set("free_join", "1");
  if (en) q.set("lang", "en");
  if (country) {
    const c = country.trim().toUpperCase().slice(0, 2);
    if (/^[A-Z]{2}$/.test(c)) q.set("country", c);
  }
  return `${path}?${q.toString()}`;
}

/** Página donde se revela el link del grupo free. */
export function buildFreeJoinReadyUrl(opts?: {
  language?: string | null;
  country?: string | null;
}): string {
  const lang = opts?.language ?? readFreeJoinLanguage();
  const country = opts?.country ?? readFreeJoinCountry();
  const base = isEnglishLang(lang) ? FREE_JOIN_ROUTE.EN : FREE_JOIN_ROUTE.ES;
  if (country) {
    const c = country.trim().toUpperCase().slice(0, 2);
    if (/^[A-Z]{2}$/.test(c)) return `${base}?country=${encodeURIComponent(c)}`;
  }
  return base;
}

/** Registro Clerk full-page fallback (sin sheet / sin Clerk en embed). */
export function buildFreeJoinRegistroUrl(opts?: {
  language?: string | null;
  country?: string | null;
}): string {
  const lang = opts?.language ?? readFreeJoinLanguage();
  const country = opts?.country ?? readFreeJoinCountry();
  const en = isEnglishLang(lang);
  const q = new URLSearchParams({
    access_request: "1",
    plan_tier: "free",
    free_join: "1",
  });
  if (en) q.set("lang", "en");
  if (country) {
    const c = country.trim().toUpperCase().slice(0, 2);
    if (/^[A-Z]{2}$/.test(c)) q.set("country", c);
  }
  return `${MEMBERSHIP_ROUTE.REGISTRO}?${q.toString()}`;
}
